import { Application, Container, Graphics, Text } from "pixi.js";
import { BENCHES, TREE_OBSTACLES, WORLD, type DrawingGameSummary, type LocalChatMessage, type MacaoSummary } from "@aproape/shared";
import { AvatarView, loadAvatarSprites, type AvatarSnapshot } from "./avatar";
import { fountainDropletPosition } from "./animation";
import { clampWorldZoom, zoomFromPinch, zoomFromWheel } from "./zoom";

export interface WorldCallbacks { onAvatarTap: (snapshot: AvatarSnapshot) => void; onDrawingTableTap: () => void; onMacaoTableTap: () => void }

export class GameWorld {
  private readonly app = new Application();
  private resizeObserver?: ResizeObserver;
  private readonly stage = new Container();
  private readonly avatars = new Map<string, AvatarView>();
  private readonly depthObjects: Container[] = [];
  private fountainJets?: Container;
  private fountainWaterGlint?: Container;
  private readonly fountainRipples: Graphics[] = [];
  private readonly fountainDroplets: Graphics[] = [];
  private fountainPhase = 0;
  private drawingTableStatus?: Text;
  private drawingTableCount?: Text;
  private macaoTableStatus?: Text;
  private macaoTableCount?: Text;
  private selfId = "";
  private cameraX: number = WORLD.spawn.x;
  private cameraY: number = WORLD.spawn.y;
  private zoom = 1;
  private inputX = 0;
  private inputY = 0;

  constructor(private readonly host: HTMLElement, private readonly callbacks: WorldCallbacks) {}

  async init(): Promise<void> {
    await this.app.init({ width: Math.max(1, this.host.clientWidth), height: Math.max(1, this.host.clientHeight), antialias: true, background: "#f4d7b4", resolution: Math.min(devicePixelRatio, 2), autoDensity: true, preference: "webgl" });
    await loadAvatarSprites();
    this.host.prepend(this.app.canvas);
    this.app.stage.addChild(this.stage);
    this.drawMap();
    this.app.ticker.add((ticker) => this.update(ticker.deltaMS / 1000));
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host);
    this.resize();
  }

  resize(): void {
    const width = Math.max(1, this.host.clientWidth);
    const height = Math.max(1, this.host.clientHeight);
    if (this.app.renderer.width !== width || this.app.renderer.height !== height) this.app.renderer.resize(width, height);
    this.zoom = clampWorldZoom(this.zoom, this.minimumZoom());
  }

  getZoom(): number { return this.zoom; }
  zoomWithWheel(deltaY: number, clientX: number, clientY: number): void {
    this.setZoomAround(zoomFromWheel(this.zoom, deltaY, this.minimumZoom()), clientX, clientY);
  }
  zoomWithPinch(startZoom: number, startDistance: number, currentDistance: number, clientX: number, clientY: number): void {
    this.setZoomAround(zoomFromPinch(startZoom, startDistance, currentDistance, this.minimumZoom()), clientX, clientY);
  }

  private minimumZoom(): number {
    return Math.max(0.65, this.host.clientWidth / WORLD.width, this.host.clientHeight / WORLD.height);
  }

  private setZoomAround(nextZoom: number, clientX: number, clientY: number): void {
    const rect = this.host.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const worldX = (localX - this.stage.x) / this.zoom;
    const worldY = (localY - this.stage.y) / this.zoom;
    this.zoom = nextZoom;
    this.cameraX = worldX + (this.host.clientWidth / 2 - localX) / this.zoom;
    this.cameraY = worldY + (this.host.clientHeight / 2 - localY) / this.zoom;
  }

  private drawMap(): void {
    this.stage.addChild(new Graphics().roundRect(0, 0, WORLD.width, WORLD.height, 60).fill(0xf5ddb8));
    const plaza = new Graphics().circle(WORLD.spawn.x, WORLD.spawn.y, 330).fill(0xf8c9ad).stroke({ color: 0xffead5, width: 18 });
    this.stage.addChild(plaza);
    const paths = new Graphics()
      .roundRect(0, WORLD.spawn.y - 54, WORLD.width, 108, 52).fill(0xf9e8cf)
      .roundRect(WORLD.spawn.x - 54, 0, 108, WORLD.height, 52).fill(0xf9e8cf);
    this.stage.addChild(paths);
    this.drawEntranceGate();
    this.drawStatue();
    this.addZone(720, 345, 0xaadcc8, "Grădina cu povești", "✿");
    this.addZone(1480, 345, 0xb8cceb, "Terasa cu soare", "☀");
    this.addZone(720, 1350, 0xd8c0ed, "Colțul liniștit", "☾");
    this.addZone(1480, 1350, 0xf6c5c8, "Piațeta roz", "♡");
    BENCHES.forEach((bench, index) => this.drawBench(bench, index));
    TREE_OBSTACLES.forEach((tree, index) => this.drawTree(tree.x, tree.y, index));
    this.drawFountain();
    this.drawDrawingTable();
    this.drawMacaoTable();
  }

  private drawMacaoTable(): void {
    const table = new Container();
    table.position.set(WORLD.macaoTable.x, WORLD.macaoTable.y);
    table.eventMode = "none";
    table.addChild(
      new Graphics().ellipse(0, 18, 112, 35).fill({ color: 0x453b51, alpha: .2 }),
      new Graphics().roundRect(-96, -62, 192, 90, 34).fill(0x6f8b70).stroke({ color: 0x40594a, width: 7 }),
      new Graphics().roundRect(-78, -47, 156, 58, 23).fill(0x467356).stroke({ color: 0x365443, width: 4 }),
      new Graphics().roundRect(-77, 19, 24, 60, 9).fill(0x4d5f4f).roundRect(53, 19, 24, 60, 9).fill(0x4d5f4f),
      new Graphics().roundRect(-35, -35, 43, 58, 6).fill(0xfffaf4).stroke({ color: 0xd6c8bf, width: 3 }),
      new Graphics().roundRect(-25, -42, 43, 58, 6).fill(0xfffaf4).stroke({ color: 0xd6c8bf, width: 3 }),
    );
    const suit = new Text({ text: "♥", style: { fontFamily: "Arial", fontSize: 27, fontWeight: "800", fill: 0xd85f67 } });
    suit.anchor.set(.5); suit.position.set(-3, -13); table.addChild(suit);
    const title = new Text({ text: "Macaua", style: { fontFamily: "Arial", fontSize: 21, fontWeight: "800", fill: 0x3f3542, stroke: { color: 0xfff5e8, width: 5 } } });
    title.anchor.set(.5, 1); title.position.set(0, -84); table.addChild(title);
    this.macaoTableStatus = new Text({ text: "Așteaptă jucători", style: { fontFamily: "Arial", fontSize: 13, fontWeight: "700", fill: 0x466950, stroke: { color: 0xfff5e8, width: 4 } } });
    this.macaoTableStatus.anchor.set(.5, 1); this.macaoTableStatus.position.set(0, -66); table.addChild(this.macaoTableStatus);
    this.macaoTableCount = new Text({ text: "0/4", style: { fontFamily: "Arial", fontSize: 12, fontWeight: "800", fill: 0xffffff } });
    this.macaoTableCount.anchor.set(.5); this.macaoTableCount.position.set(0, 49);
    table.addChild(new Graphics().roundRect(-29, 37, 58, 25, 12).fill(0x3f4f43), this.macaoTableCount);
    this.depthObjects.push(table); this.stage.addChild(table);
  }

  setMacaoTableStatus(summary?: MacaoSummary): void {
    if (this.macaoTableStatus) this.macaoTableStatus.text = summary?.statusLabel ?? "Așteaptă jucători";
    if (this.macaoTableCount) this.macaoTableCount.text = summary ? `${summary.playerCount}/${summary.maxPlayers}` : "0/4";
  }

  private drawDrawingTable(): void {
    const table = new Container();
    table.position.set(WORLD.drawingTable.x, WORLD.drawingTable.y);
    table.eventMode = "none";
    table.addChild(
      new Graphics().ellipse(0, 18, 112, 35).fill({ color: 0x61483f, alpha: .18 }),
      new Graphics().roundRect(-96, -62, 192, 90, 34).fill(0xd99270).stroke({ color: 0x84504a, width: 7 }),
      new Graphics().roundRect(-78, -47, 156, 58, 23).fill(0xf7e8d6).stroke({ color: 0xbc725e, width: 4 }),
      new Graphics().roundRect(-77, 19, 24, 60, 9).fill(0x8f5b4d).roundRect(53, 19, 24, 60, 9).fill(0x8f5b4d),
      new Graphics().roundRect(-58, -34, 79, 38, 7).fill(0xffffff).stroke({ color: 0xd8c5bc, width: 3 }),
      new Graphics().moveTo(-45, -4).lineTo(8, -29).stroke({ color: 0x4f586f, width: 5 }).circle(8, -29, 5).fill(0xed816d),
      new Graphics().roundRect(31, -35, 30, 8, 4).fill(0x6f8fc4).roundRect(31, -22, 30, 8, 4).fill(0xe5a64c).roundRect(31, -9, 30, 8, 4).fill(0x66ad79),
    );
    const title = new Text({ text: "Desenează și Ghicește", style: { fontFamily: "Arial", fontSize: 19, fontWeight: "800", fill: 0x543f49, stroke: { color: 0xfff5e8, width: 5 } } });
    title.anchor.set(.5, 1); title.position.set(0, -84); table.addChild(title);
    this.drawingTableStatus = new Text({ text: "Așteaptă jucători", style: { fontFamily: "Arial", fontSize: 13, fontWeight: "700", fill: 0xb45d50, stroke: { color: 0xfff5e8, width: 4 } } });
    this.drawingTableStatus.anchor.set(.5, 1); this.drawingTableStatus.position.set(0, -66); table.addChild(this.drawingTableStatus);
    this.drawingTableCount = new Text({ text: "0/8", style: { fontFamily: "Arial", fontSize: 12, fontWeight: "800", fill: 0xffffff } });
    this.drawingTableCount.anchor.set(.5); this.drawingTableCount.position.set(0, 49);
    const countPill = new Graphics().roundRect(-29, 37, 58, 25, 12).fill(0x57434d); table.addChild(countPill, this.drawingTableCount);
    this.depthObjects.push(table); this.stage.addChild(table);
  }

  setDrawingTableStatus(summary?: DrawingGameSummary): void {
    if (this.drawingTableStatus) this.drawingTableStatus.text = summary?.statusLabel ?? "Așteaptă jucători";
    if (this.drawingTableCount) this.drawingTableCount.text = summary ? `${summary.playerCount}/${summary.maxPlayers}` : "0/8";
  }

  private drawEntranceGate(): void {
    const gate = new Container();
    gate.position.set(WORLD.spawn.x, 105);
    const stone = 0xc69b78;
    const stoneLight = 0xe8c7a5;
    const stoneDark = 0x8d6657;
    gate.addChild(
      new Graphics().ellipse(0, 27, 168, 28).fill({ color: 0x735448, alpha: 0.14 }),
      new Graphics().roundRect(-156, -100, 48, 132, 10).fill(stone).stroke({ color: stoneDark, width: 5 }),
      new Graphics().roundRect(108, -100, 48, 132, 10).fill(stone).stroke({ color: stoneDark, width: 5 }),
      new Graphics().roundRect(-166, -113, 68, 23, 8).fill(stoneLight).stroke({ color: stoneDark, width: 4 }),
      new Graphics().roundRect(98, -113, 68, 23, 8).fill(stoneLight).stroke({ color: stoneDark, width: 4 }),
      new Graphics()
        .moveTo(-132, -92)
        .bezierCurveTo(-93, -181, 93, -181, 132, -92)
        .stroke({ color: stoneDark, width: 28 })
        .moveTo(-132, -94)
        .bezierCurveTo(-91, -157, 91, -157, 132, -94)
        .stroke({ color: stoneLight, width: 17 }),
    );
    this.depthObjects.push(gate);
    this.stage.addChild(gate);
  }

  private drawStatue(): void {
    const statue = new Container();
    statue.position.set(WORLD.statue.x, WORLD.statue.y);
    const bronze = 0xb77752;
    const bronzeLight = 0xd59a70;
    const bronzeDark = 0x754b43;
    statue.addChild(
      new Graphics().ellipse(0, 7, 72, 25).fill({ color: 0x63483f, alpha: 0.2 }),
      new Graphics().roundRect(-55, -30, 110, 38, 12).fill(0xcaa786).stroke({ color: 0x8b6b5a, width: 5 }),
      new Graphics().roundRect(-40, -73, 80, 47, 9).fill(0xe0c2a1).stroke({ color: 0x967462, width: 5 }),
      new Graphics().roundRect(-17, -126, 14, 55, 7).fill(bronzeDark),
      new Graphics().roundRect(3, -126, 14, 55, 7).fill(bronzeDark),
      new Graphics().roundRect(-34, -179, 68, 62, 25).fill(bronze).stroke({ color: bronzeDark, width: 5 }),
      new Graphics().moveTo(-27, -165).lineTo(-55, -137).moveTo(27, -165).lineTo(56, -196).stroke({ color: bronzeDark, width: 14 }),
      new Graphics().circle(0, -214, 34).fill(bronzeLight).stroke({ color: bronzeDark, width: 5 }),
      new Graphics().moveTo(-29, -225).bezierCurveTo(-18, -257, 19, -257, 31, -224).lineTo(21, -237).lineTo(9, -226).lineTo(-3, -241).lineTo(-15, -226).closePath().fill(bronzeDark),
      new Graphics().circle(-11, -215, 3).fill(bronzeDark).circle(11, -215, 3).fill(bronzeDark).arc(0, -207, 10, 0.2, Math.PI - 0.2).stroke({ color: bronzeDark, width: 3 }),
    );
    const plaque = new Text({ text: "Statuie în fața porții", style: { fontFamily: "Arial", fontSize: 16, fontWeight: "700", fill: 0x6d5048, stroke: { color: 0xfff2dc, width: 4 } } });
    plaque.anchor.set(0.5, 0);
    plaque.position.set(0, 19);
    statue.addChild(plaque);
    this.depthObjects.push(statue);
    this.stage.addChild(statue);
  }

  private drawBench(bench: (typeof BENCHES)[number], variant: number): void {
    const structure = new Container();
    structure.position.set(bench.x, bench.y);
    const half = bench.width / 2;
    const heartX = variant % 2 === 0 ? -half + 36 : half - 36;
    structure.addChild(
      new Graphics().ellipse(0, 18, half + 22, 30).fill({ color: 0x694e48, alpha: 0.18 }),
      new Graphics().roundRect(-half + 8, -122, bench.width - 16, 72, 18).fill(bench.wood).stroke({ color: bench.edge, width: 6 }),
      new Graphics().moveTo(-half + 23, -96).lineTo(half - 23, -96).moveTo(-half + 23, -72).lineTo(half - 23, -72).stroke({ color: bench.accent, width: 7, alpha: 0.8 }),
      new Graphics().roundRect(-half - 8, -55, bench.width + 16, 38, 13).fill(bench.wood).stroke({ color: bench.edge, width: 6 }),
      new Graphics().roundRect(-half + 18, -28, 25, 58, 9).fill(bench.edge).roundRect(half - 43, -28, 25, 58, 9).fill(bench.edge),
      new Graphics().roundRect(-half - 18, -78, 25, 85, 10).fill(bench.edge).roundRect(half - 7, -78, 25, 85, 10).fill(bench.edge),
      new Graphics().circle(heartX - 7, -87, 9).fill(bench.accent).circle(heartX + 7, -87, 9).fill(bench.accent).poly([heartX - 16, -86, heartX + 16, -86, heartX, -66]).fill(bench.accent),
    );
    const sign = new Text({ text: bench.label, style: { fontFamily: "Arial", fontSize: 16, fontWeight: "700", fill: bench.edge, stroke: { color: 0xfff5e7, width: 4 } } });
    sign.anchor.set(0.5, 1);
    sign.position.set(0, -137);
    structure.addChild(sign);
    this.depthObjects.push(structure);
    this.stage.addChild(structure);
  }

  private drawTree(x: number, y: number, variant: number): void {
    const tree = new Container();
    tree.position.set(x, y);
    const dark = variant % 3 === 0 ? 0x397a55 : variant % 3 === 1 ? 0x3f865d : 0x467c50;
    const mid = variant % 3 === 0 ? 0x58a66f : variant % 3 === 1 ? 0x66ae76 : 0x5d9d61;
    const light = variant % 3 === 0 ? 0x80c58c : variant % 3 === 1 ? 0x8bca91 : 0x7fba78;
    tree.addChild(
      new Graphics().ellipse(0, 3, 61, 22).fill({ color: 0x684d43, alpha: 0.2 }),
      new Graphics().roundRect(-17, -105, 34, 108, 13).fill(0x9b6847).stroke({ color: 0x714a37, width: 4 }),
      new Graphics().moveTo(-8, -74).lineTo(-43, -121).moveTo(9, -82).lineTo(47, -132).stroke({ color: 0x81543d, width: 12 }),
      new Graphics().circle(-36, -137, 57).fill(dark).circle(37, -145, 62).fill(dark).circle(0, -187, 67).fill(dark),
      new Graphics().circle(-45, -153, 48).fill(mid).circle(37, -164, 53).fill(mid).circle(0, -202, 53).fill(mid),
      new Graphics().circle(-20, -212, 31).fill(light).circle(24, -197, 34).fill(light).circle(-51, -170, 25).fill(light),
    );
    this.depthObjects.push(tree);
    this.stage.addChild(tree);
  }

  private drawFountain(): void {
    const fountain = new Container();
    fountain.position.set(WORLD.fountain.x, WORLD.fountain.y);
    fountain.eventMode = "none";
    fountain.addChild(
      new Graphics().ellipse(0, 24, 108, 57).fill({ color: 0x76584f, alpha: 0.2 }),
      new Graphics().circle(0, 0, WORLD.fountain.visualRadius + 5).fill(0xc9a78f).stroke({ color: 0xa88070, width: 3 }),
      new Graphics().circle(0, -3, WORLD.fountain.visualRadius).fill(0xfff0dc).stroke({ color: 0xe1c5ab, width: 6 }),
      new Graphics().circle(0, -3, 74).fill(0x63bfd9).stroke({ color: 0xcff5fb, width: 5 }),
      new Graphics().circle(0, -3, 66).fill(0x82d5e7),
      new Graphics().arc(0, -3, 58, 3.48, 5.73).stroke({ color: 0xdaf8ff, width: 5, alpha: 0.75 }),
    );

    const glint = new Container();
    glint.position.set(0, -3);
    glint.addChild(
      new Graphics().arc(0, 0, 48, -2.75, -1.08).stroke({ color: 0xffffff, width: 3, alpha: 0.52 }),
      new Graphics().arc(0, 0, 36, 0.25, 1.45).stroke({ color: 0xbaf1fb, width: 2, alpha: 0.65 }),
      new Graphics().circle(-38, 16, 3).fill({ color: 0xe9fcff, alpha: 0.75 }).circle(40, -13, 2).fill({ color: 0xe9fcff, alpha: 0.75 }),
    );
    this.fountainWaterGlint = glint;
    fountain.addChild(glint);

    const ripples = new Container();
    ripples.position.set(0, -3);
    for (let index = 0; index < 3; index += 1) {
      const ripple = new Graphics().ellipse(0, 0, 23, 10).stroke({ color: 0xe9fcff, width: 2.5, alpha: 0.8 });
      this.fountainRipples.push(ripple);
      ripples.addChild(ripple);
    }
    fountain.addChild(ripples);

    fountain.addChild(
      new Graphics().circle(0, -3, 25).fill(0xefd2b7).stroke({ color: 0xbd927e, width: 5 }),
      new Graphics().ellipse(0, -34, 29, 10).fill(0xe6c1a4).stroke({ color: 0xb98874, width: 4 }),
      new Graphics().roundRect(-10, -38, 20, 39, 7).fill(0xe4c1a5).stroke({ color: 0xb88c78, width: 3 }),
      new Graphics().ellipse(0, -41, 19, 7).fill(0xffead2).stroke({ color: 0xb98c77, width: 3 }),
      new Graphics().roundRect(-5, -68, 10, 28, 5).fill(0xe8c7aa).stroke({ color: 0xb88c78, width: 2 }),
      new Graphics().circle(0, -70, 9).fill(0xffead2).stroke({ color: 0xba8c76, width: 3 }),
    );

    const jets = new Container();
    const streams = new Graphics()
      .moveTo(-57, -4).bezierCurveTo(-48, -54, -20, -91, 0, -69)
      .moveTo(57, -4).bezierCurveTo(48, -54, 20, -91, 0, -69)
      .moveTo(-42, -10).bezierCurveTo(-34, -49, -15, -70, 0, -42)
      .moveTo(42, -10).bezierCurveTo(34, -49, 15, -70, 0, -42)
      .moveTo(0, -70).bezierCurveTo(-4, -94, 4, -112, 0, -124)
      .stroke({ color: 0x70cce4, width: 8, alpha: 0.45 })
      .moveTo(-57, -4).bezierCurveTo(-48, -54, -20, -91, 0, -69)
      .moveTo(57, -4).bezierCurveTo(48, -54, 20, -91, 0, -69)
      .moveTo(-42, -10).bezierCurveTo(-34, -49, -15, -70, 0, -42)
      .moveTo(42, -10).bezierCurveTo(34, -49, 15, -70, 0, -42)
      .moveTo(0, -70).bezierCurveTo(-4, -94, 4, -112, 0, -124)
      .stroke({ color: 0xe4fbff, width: 4, alpha: 0.94 });
    jets.addChild(streams);
    for (let index = 0; index < 12; index += 1) {
      const droplet = new Graphics().circle(0, 0, index % 3 === 0 ? 3.8 : 2.8).fill({ color: index % 3 === 0 ? 0xe7fcff : 0xa7e7f4, alpha: 0.95 });
      this.fountainDroplets.push(droplet);
      jets.addChild(droplet);
    }
    jets.addChild(new Graphics().circle(0, -128, 5).fill(0xf0fdff).circle(-3, -117, 2.5).fill(0xc4f2fa).circle(4, -108, 2).fill(0xe8fcff));
    fountain.addChild(jets);
    const label = new Text({ text: "Fântâna întâlnirilor", style: { fontFamily: "Arial", fontSize: 17, fontWeight: "600", fill: 0x6f5360, stroke: { color: 0xfff4e6, width: 4 } } });
    label.anchor.set(0.5, 0); label.position.set(0, 104); fountain.addChild(label);
    this.fountainJets = jets;
    this.depthObjects.push(fountain);
    this.stage.addChild(fountain);
  }

  private addZone(x: number, y: number, color: number, label: string, symbol: string): void {
    this.stage.addChild(new Graphics().circle(x, y, 105).fill({ color, alpha: 0.75 }).stroke({ color: 0xffffff, width: 8, alpha: 0.7 }));
    const icon = new Text({ text: symbol, style: { fontFamily: "Arial", fontSize: 52, fill: 0xffffff } }); icon.anchor.set(0.5); icon.position.set(x, y - 4); this.stage.addChild(icon);
    const text = new Text({ text: label, style: { fontFamily: "Arial", fontSize: 18, fontWeight: "600", fill: 0x5b4d57 } }); text.anchor.set(0.5); text.position.set(x, y + 132); this.stage.addChild(text);
  }

  setSelf(id: string): void { this.selfId = id; }
  syncPlayers(players: AvatarSnapshot[]): void {
    const present = new Set(players.map((p) => p.id));
    for (const snapshot of players) {
      let view = this.avatars.get(snapshot.id);
      if (view && view.snapshot.avatarParts.top !== snapshot.avatarParts.top) {
        this.stage.removeChild(view);
        view.destroy({ children: true });
        this.avatars.delete(snapshot.id);
        view = undefined;
      }
      if (!view) { view = new AvatarView({ ...snapshot }, snapshot.id === this.selfId); this.avatars.set(snapshot.id, view); this.stage.addChild(view); }
      view.setServerSnapshot(snapshot);
    }
    for (const [id, view] of this.avatars) if (!present.has(id)) { this.stage.removeChild(view); view.destroy({ children: true }); this.avatars.delete(id); }
  }
  predictSelf(x: number, y: number, delta: number): void { this.avatars.get(this.selfId)?.predict(x, y, delta); }
  setInput(x: number, y: number): void { this.inputX = x; this.inputY = y; }
  showChat(message: LocalChatMessage): void {
    const sender = this.avatars.get(message.senderId) ?? [...this.avatars.values()].find((avatar) => avatar.snapshot.displayName === message.displayName);
    sender?.showBubble(message.text, Date.now() + WORLD.chatBubbleMs);
  }
  showTyping(senderId: string, active: boolean): void { this.avatars.get(senderId)?.setTyping(active); }
  isAvatarTyping(name: string): boolean { return [...this.avatars.values()].find((avatar) => avatar.snapshot.displayName === name)?.isTyping() ?? false; }
  tapAvatarByName(name: string): boolean {
    const avatar = [...this.avatars.values()].find((entry) => entry.snapshot.displayName === name);
    if (!avatar) return false;
    this.callbacks.onAvatarTap(avatar.snapshot);
    return true;
  }
  tapDrawingTable(): boolean {
    this.callbacks.onDrawingTableTap();
    return true;
  }
  tapMacaoTable(): boolean { this.callbacks.onMacaoTableTap(); return true; }

  tapAt(clientX: number, clientY: number): void {
    const rect = this.host.getBoundingClientRect();
    const worldX = (clientX - rect.left - this.stage.x) / this.zoom;
    const worldY = (clientY - rect.top - this.stage.y) / this.zoom;
    if (Math.hypot(worldX - WORLD.drawingTable.x, worldY - (WORLD.drawingTable.y - 25)) <= WORLD.drawingTable.interactionRadius) {
      this.callbacks.onDrawingTableTap();
      return;
    }
    if (Math.hypot(worldX - WORLD.macaoTable.x, worldY - (WORLD.macaoTable.y - 25)) <= WORLD.macaoTable.interactionRadius) {
      this.callbacks.onMacaoTableTap();
      return;
    }
    const candidates = [...this.avatars.values()].filter((avatar) => avatar.snapshot.id !== this.selfId && Math.abs(avatar.x - worldX) <= 52 && worldY >= avatar.y - 210 && worldY <= avatar.y + 24).sort((a, b) => b.y - a.y);
    if (candidates[0]) { this.callbacks.onAvatarTap(candidates[0].snapshot); return; }
  }

  private update(delta: number): void {
    this.fountainPhase += delta;
    if (this.fountainJets) {
      this.fountainJets.alpha = 0.91 + Math.sin(this.fountainPhase * 3.2) * 0.07;
      this.fountainJets.scale.y = 0.99 + Math.sin(this.fountainPhase * 2.4) * 0.018;
    }
    if (this.fountainWaterGlint) this.fountainWaterGlint.rotation = this.fountainPhase * 0.13;
    this.fountainRipples.forEach((ripple, index) => {
      const progress = (this.fountainPhase * 0.42 + index / this.fountainRipples.length) % 1;
      ripple.scale.set(0.65 + progress * 1.45);
      ripple.alpha = (1 - progress) * 0.55;
    });
    this.fountainDroplets.forEach((droplet, index) => {
      const position = fountainDropletPosition(index, this.fountainPhase);
      droplet.position.set(position.x, position.y);
      droplet.alpha = position.alpha;
      droplet.scale.set(position.scale);
    });
    this.predictSelf(this.inputX, this.inputY, Math.min(delta, 0.05));
    for (const avatar of this.avatars.values()) avatar.update(delta);
    const self = this.avatars.get(this.selfId);
    if (self) { this.cameraX += (self.x - this.cameraX) * 0.09; this.cameraY += (self.y - this.cameraY) * 0.09; }
    const width = this.host.clientWidth, height = this.host.clientHeight;
    this.zoom = clampWorldZoom(this.zoom, this.minimumZoom());
    const halfW = width / (2 * this.zoom), halfH = height / (2 * this.zoom);
    this.cameraX = Math.max(halfW, Math.min(WORLD.width - halfW, this.cameraX));
    this.cameraY = Math.max(halfH, Math.min(WORLD.height - halfH, this.cameraY));
    this.stage.scale.set(this.zoom);
    this.stage.position.set(Math.round(width / 2 - this.cameraX * this.zoom), Math.round(height / 2 - this.cameraY * this.zoom));
    const sorted = [...this.depthObjects, ...this.avatars.values()].sort((a, b) => a.y - b.y);
    for (const object of sorted) this.stage.setChildIndex(object, this.stage.children.length - 1);
  }
  destroy(): void { this.resizeObserver?.disconnect(); this.app.destroy(true, { children: true }); }
}
